import { useQueryClient } from "@tanstack/react-query";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import TrainTravelClassDropdown from "components/dropdowns/reactselect/TrainTravelClassDropdown";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { GooglePlaceState } from "shared/utils/fieldsHelper/groundFields";
import { GooglePlaceInputWithValue } from "components/ValidatedFields/GoolgePlaceInputWithValue";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useCallback, useEffect } from "react";
import { invalidateTrainsCache } from "utilities/api/trainQuery";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenTrainSidePanel } from "./TrainSidePanel";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import { getCreateTrainInstruction, getInitialNewTrainFormState, NewTrainFormState } from "shared/utils/fieldsHelper/newTrainFields";

interface Props {
  fromTrain?: server.dto.TrainSidePanelData
}

const NewTrainSidePanel = (props: Props) => {
  const planningContext = React.useContext(PlanningContext);
  const openTrainSidePanel = useOpenTrainSidePanel();

  const [value, setValue] = React.useState<NewTrainFormState>(
    getInitialNewTrainFormState(serverReference, props.fromTrain, planningContext.opts?.artist, planningContext.opts?.date)
  );

  const queryClient = useQueryClient();

  const submit = () => {
    return window.CoreApi.Train.CreateTrain(getCreateTrainInstruction(value, planningContext.opts?.fromShowId));
  }

  useEffect(() => {
    const fetchTravellers = async () => {
      if (value.artist?.artistId) {
        const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
          companyId: value.artist.artistId,
          defaultSelect: true,
          isActive: true,
          page: 1,
          perPage: 1000,
        });

        if (result.data.hasErrors) {
          return;
        }

        onChange({
          travellers: result.data.data.map(c => ({
            contactId: c.contact.contactId,
            culture: c.contact.culture,
            gender: c.contact.gender,
            isValidated: c.contact.isValidated,
            name: c.contact.name,
            position: c.contact.position,
            profileImageUrl: c.contact.profileImageUrl,
          }))
        });
      }
    }
    fetchTravellers();
  }, [value.artist?.artistId])

  const onChange = (newData: Partial<NewTrainFormState>) => {
    setValue({ ...value, ...newData });
  };

  return <SidePanelNewSectionTemplate
    afterCreate="displayItemInSidebar"
    onSave={id => {
      invalidateTrainsCache(queryClient);
      openTrainSidePanel(id);
    }}
    title={RESX.Train.NewTrainJourney}
    id="new-train-side-panel"
    trigger={null}
    submit={submit}
    sections={[{
      key: "train",
      type: "regular",
      hideTitle: true,
      title: RESX.Train.resxTrain,
      fields: [{
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        render: () => <div className="main-section-template-fields-padding">
          <ArtistModalPicker
            securityScope="limitByUserAccess"
            canCreateNew={false}
            nonInPageEditing={{
              onChange: artist => onChange({ artist }),
              value: value.artist,
              validatorKey: "artist",
              required: true,
            }}
          />
        </div>
      }, {
        key: "travellers",
        label: RESX.Flight.Travellers,
        render: () => <div className="main-section-template-fields-padding">
          <ContactMultiModalPicker
            value={value.travellers}
            onChange={travellers => onChange({ travellers })}
            canCreateNew={false}
            required={false}
            validatorKey="contacts"
          />
        </div>
      }, {
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => <AppointmentPhaseDropdown
          value={value.phase}
          onChange={phase => onChange({ phase })}
        />
      }, {
        key: "bookingReference",
        label: RESX.Flight.BookingCode,
        render: () => <OptionalTextInput
          maxLength={serverReference.constants.train.maxBookingCodeLength}
          value={value.bookingReference}
          onChange={bookingReference => onChange({ bookingReference })}
        />
      }, {
        key: "travelAgency",
        label: RESX.Flight.TravelAgency,
        render: () => <OptionalTextInput
          maxLength={serverReference.constants.train.maxTravelAgencyLength}
          value={value.travelAgency}
          onChange={travelAgency => onChange({ travelAgency })}
        />
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        render: () => <OptionalTextAreaInput
          maxLength={serverReference.constants.groundTransport.maxNotesLength}
          rows={3}
          value={value.notes}
          onChange={notes => onChange({ notes })}
        />
      }, {
        key: "trainNumber",
        label: RESX.Train.TrainNumber,
        render: () => <OptionalTextInput
          width={100}
          maxLength={serverReference.constants.train.maxTrainNumberLength}
          value={value.trainNumber}
          onChange={trainNumber => onChange({ trainNumber })}
        />
      }, {
        key: "carriage",
        label: RESX.Train.Carriage,
        render: () => <OptionalTextInput
          width={100}
          maxLength={serverReference.constants.train.maxCarriageLength}
          value={value.carriage}
          onChange={carriage => onChange({ carriage })}
        />
      }, {
        key: "seat",
        label: RESX.Flight.Seats,
        render: () => <OptionalTextInput
          width={200}
          maxLength={serverReference.constants.train.maxSeatsLength}
          value={value.seat}
          onChange={seat => onChange({ seat })}
        />
      }, {
        key: "travelClass",
        label: RESX.Flight.TravelClass,
        render: () => <TrainTravelClassDropdown
          value={value.travelClass}
          onChange={travelClass => onChange({ travelClass })}
        />
      }]
    }, {
      key: "departureArrival",
      type: "regular",
      hideTitle: true,
      title: "",
      fields: [{
        key: "departureArrivalLabel",
        label: "",
        render: () => <div className="main-section-template-two-column-grid">
          <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Departure}</h3>
          <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Arrival}</h3>
        </div>
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        render: () => <div className="main-section-template-two-column-grid">
          <DateTimePicker
            value={value.departureDate}
            onChange={departureDate => {
              const diff = departureDate.diff(value.departureDate, "minutes");
              const newArrivalDate = value.arrivalDate.clone().add(diff, "minutes");
              onChange({ departureDate, arrivalDate: newArrivalDate })
            }}
            validatorKey="departureDate"
            minDate={moment(serverReference.constants.appointment.minDate)}
            maxDate={moment(serverReference.constants.appointment.maxDate)}
          />
          <DateTimePicker
            value={value.arrivalDate}
            onChange={arrivalDate => onChange({ arrivalDate })}
            validatorKey="arrivalDate"
            minDate={value.departureDate.clone().add(serverReference.constants.train.maxArrivalBeforeDepartureHours, "hours")}
            maxDate={value.departureDate.clone().add(serverReference.constants.train.maxArrivalAfterDepartureHours, "hours")}
          />
        </div>
      }, {
        key: "station",
        label: RESX.Train.Station,
        render: () => <div className="main-section-template-two-column-grid">
          <GooglePlaceInputWithValue
            isRequired
            validatorKey="departure-station"
            value={value.departureStation}
            calledFrom="train"
            placeType={"trainStation"}
            onPlaceSelected={(v: GooglePlaceState) => {
              onChange({ departureStation: v });
            }}
          />
          <GooglePlaceInputWithValue
            isRequired
            validatorKey="arrival-station"
            value={value.arrivalStation}
            calledFrom="train"
            placeType={"trainStation"}
            onPlaceSelected={(v: GooglePlaceState) => {
              onChange({ arrivalStation: v });
            }}
          />
        </div>
      }, {
        key: "platform",
        label: RESX.Train.Platform,
        render: () => <div className="main-section-template-two-column-grid">
          <OptionalTextInput
            width={60}
            value={value.departurePlatform}
            onChange={departurePlatform => onChange({ departurePlatform })}
            maxLength={serverReference.constants.train.maxPlatformLength}
          />
          <OptionalTextInput
            width={60}
            value={value.arrivalPlatform}
            onChange={arrivalPlatform => onChange({ arrivalPlatform })}
            maxLength={serverReference.constants.train.maxPlatformLength}
          />
        </div>
      }]
    }]}
  />
}

export const useOpenNewTrainSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewTrainSidePanel = useCallback(async (addingSegmentFromTrainId?: number) => {
    let fromTrain: server.dto.TrainSidePanelData = undefined;
    if (addingSegmentFromTrainId) {
      const result = await window.CoreApi.Train.GetSidePanelData(addingSegmentFromTrainId);
      fromTrain = result.data.data;
    }
    openSidePanel(
      <NewTrainSidePanel fromTrain={fromTrain} />,
      "new-train-side-panel",
      "large",
    );
  }, [openSidePanel]);

  return openNewTrainSidePanel;
}